import { glassTextures } from '../data/mockData';

/** Config Go quote line — Double Glass 24 mm (HR++) */
export const VMK_GLAZING_LABEL_24 = 'HR++ - 5-15-4*';

/** Config Go quote line — Triple Glass 36 mm (HR+++) */
export const VMK_GLAZING_LABEL_36 = 'HR+++ - 4-12-4-12-4*';

/** @deprecated Use getVmkGlazingBaseLabel — kept for imports that expect 24 mm default */
export const VMK_GLAZING_LABEL = VMK_GLAZING_LABEL_24;

const GLAZING_36_IDS = new Set(['triple', 'double-36']);

/** Configurator glazingType id → 36 mm HR+++ */
export const isGlazing36mm = (glazingTypeId) =>
  GLAZING_36_IDS.has(glazingTypeId);

export const getGlazingUgValue = (glazingTypeId, t) =>
  t(isGlazing36mm(glazingTypeId)
    ? 'quote.template.specGlassUgValueTriple'
    : 'quote.template.specGlassUgValueDouble');

/** Dutch subsidy codes shown on the VMK spec page glazing block. */
export const VMK_SUBSIDY_CODE_DOUBLE = 'KA20321 HR++';
export const VMK_SUBSIDY_CODE_TRIPLE = 'KA30709 HR+++ Triple';

const quoteQuantity = (quote) => {
  const qty = Number(quote?.config?.quantity);
  return Number.isFinite(qty) && qty >= 1 ? qty : 1;
};

/** Resolve effective glazing type id from config (windows use glazingType; Schuifpui uses rightGlazingType). */
export const resolveConfigGlazingTypeId = (config) =>
  config?.glazingType || config?.rightGlazingType || config?.leftGlazingType || 'double';

/** Resolve glass texture id for VMK label suffix. */
export const resolveConfigGlassTextureId = (config) =>
  config?.glassTexture || config?.rightGlassTexture || config?.leftGlassTexture || null;

const glazingBucketForConfig = (config) => {
  if (!config) return 'double';
  const ids = [config.glazingType, config.rightGlazingType, config.leftGlazingType].filter(Boolean);
  if (ids.length === 0) return 'double';
  const tripleWeight = ids.filter((id) => isGlazing36mm(id)).length;
  return tripleWeight >= Math.ceil(ids.length / 2) ? 'triple' : 'double';
};

/** Dominant glazing across saved drawings (weighted by quantity). */
export const resolveOrderGlazingTypeId = (savedQuotes = []) => {
  const counts = { double: 0, triple: 0 };

  savedQuotes.forEach((quote) => {
    const bucket = glazingBucketForConfig(quote?.config);
    counts[bucket] += quoteQuantity(quote);
  });

  if (counts.triple > counts.double) return 'triple';
  if (counts.double > counts.triple) return 'double';

  const first = savedQuotes[0]?.config;
  if (first) return isGlazing36mm(resolveConfigGlazingTypeId(first)) ? 'triple' : 'double';
  return 'double';
};

/** i18n key for glazing headline on the VMK spec page. */
export const getGlazingSpecLeadKey = (glazingTypeId) =>
  (isGlazing36mm(glazingTypeId)
    ? 'quote.template.specGlassLeadTriple'
    : 'quote.template.specGlassLeadDouble');

/** i18n key for glazing bullet list (without subsidy code). */
export const getGlazingSpecItemsKey = (glazingTypeId) =>
  (isGlazing36mm(glazingTypeId)
    ? 'quote.template.specGlassItemsTriple'
    : 'quote.template.specGlassItemsDouble');

/** Bullet list for glazing block, with subsidy code as first item. */
export const getGlazingSpecItems = (glazingTypeId, t, { includeNijBegun = false } = {}) => {
  const subsidy = isGlazing36mm(glazingTypeId) ? VMK_SUBSIDY_CODE_TRIPLE : VMK_SUBSIDY_CODE_DOUBLE;
  const items = t(getGlazingSpecItemsKey(glazingTypeId), { returnObjects: true }) || [];
  const ugLine = t('quote.template.specGlassUgValue', { value: getGlazingUgValue(glazingTypeId, t) });
  const detailItems = items.filter((item) => !/\bu?g[- ]?waarde|\bu?g value/i.test(String(item)));
  if (includeNijBegun) {
    return [subsidy, 'V2-4-D1', ugLine, ...detailItems];
  }
  return [subsidy, ugLine, ...detailItems];
};

export const isOrderTripleGlazing = (savedQuotes = []) =>
  isGlazing36mm(resolveOrderGlazingTypeId(savedQuotes));

/** Normalise legacy ids: double / double-24 → 24 mm; triple / double-36 → 36 mm */
export const normalizeGlazingTypeId = (glazingTypeId) =>
  (isGlazing36mm(glazingTypeId) ? '36' : '24');

export const getVmkGlazingBaseLabel = (glazingTypeId) =>
  (isGlazing36mm(glazingTypeId) ? VMK_GLAZING_LABEL_36 : VMK_GLAZING_LABEL_24);

const getTextureSuffix = (glassTextureId) => {
  const texture = glassTextureId
    ? glassTextures.find((g) => g.id === glassTextureId)
    : null;
  if (!texture || texture.id === 'helder-glas' || texture.id === 'clear') {
    return '';
  }
  if (texture.name?.toUpperCase().includes('CHINCHILLA')) {
    return ' CHINCHILLA BLANK';
  }
  if (texture.name?.toUpperCase().includes('SATIN')) {
    return ' SATIJN';
  }
  return ` ${texture.name?.toUpperCase() || ''}`;
};

/** Full VMK quote glazing description (HR++ code + optional texture suffix). */
export const getVmkGlazingLabel = (config) => {
  const glazingTypeId = resolveConfigGlazingTypeId(config);
  const base = getVmkGlazingBaseLabel(glazingTypeId);
  return `${base}${getTextureSuffix(resolveConfigGlassTextureId(config))}`;
};

/** VMK quote sandwich panel line — matches selected glazing thickness (24 / 36 mm). */
export const getVmkSandwichLabel = (config) => {
  const glazingTypeId = resolveConfigGlazingTypeId(config);
  return isGlazing36mm(glazingTypeId) ? 'Sandwich 36mm' : 'Sandwich 24mm';
};
